import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  StarBorder as FavoritesIcon,
  ContactSupportOutlined as HelpIcon,
  Home as HomeIcon,
  ChatBubbleOutline as MessengerIcon,
  NotificationsNone as NotificationsIcon,
} from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useAppLayout } from '@core/contexts/LayoutContext';
import { buildBreadcrumbs } from '@core/utils/breadcrumbs';
import { getHomePath, getResolvedRoute } from '@core/utils/routes';
import LayoutBreadcrumbs from './LayoutBreadcrumbs';

const PlaceholderAction: React.FC<{
  label: string;
  icon: React.ReactNode;
}> = ({ label, icon }) => (
  <Tooltip title={label}>
    <span>
      <IconButton aria-label={label} disabled sx={{ color: 'text.secondary' }}>
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

const WebTwoContextHeader: React.FC = () => {
  const { visibleNavItems } = useAppLayout();
  const { pathname } = useLocation();

  const breadcrumbs = useMemo(() => {
    const homePath = getHomePath(visibleNavItems);
    const resolved = getResolvedRoute(pathname, visibleNavItems);
    return buildBreadcrumbs(pathname, visibleNavItems, resolved).map(
      (item) => ({
        ...item,
        icon:
          item.path === homePath ? (
            <HomeIcon sx={{ fontSize: 16 }} />
          ) : undefined,
      })
    );
  }, [pathname, visibleNavItems]);

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        pt: 1,
        pl: 3,
        pr: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, alignSelf: 'flex-end', pb: 0.5 }}>
        {breadcrumbs.length > 0 ? (
          <LayoutBreadcrumbs items={breadcrumbs} density="quiet" />
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <PlaceholderAction label="Favorites" icon={<FavoritesIcon />} />
        <PlaceholderAction label="Notifications" icon={<NotificationsIcon />} />
        <PlaceholderAction label="Messenger" icon={<MessengerIcon />} />
        <PlaceholderAction label="Help" icon={<HelpIcon />} />
      </Box>
    </Box>
  );
};

export default WebTwoContextHeader;
